import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, CheckCircle, LayoutDashboard, ShieldCheck, Sparkles, Activity } from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import MarketingTestimonials from "../components/MarketingTestimonials";
import MarketingComparisonTable from "../components/MarketingComparisonTable";
import MarketingFAQ from "../components/MarketingFAQ";
import { marketingNavLinks } from "../constants/marketingNav";

const stats = [
  { label: "Projects managed", value: "10K+" },
  { label: "Happy teams", value: "500+" },
  { label: "Security certifications", value: "ISO 27001" },
];

const WhyTeamBoard = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="cta" />

      <main>
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-screen flex items-center overflow-hidden pt-16 pb-20">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-20 items-center">
            <div className="reveal-left">
              <span className="section-eyebrow">Why PMS</span>
              <h1 className="mt-6 mb-8 text-gray-900">
                A more confident way to <span className="text-blue-600">manage complex projects.</span>
              </h1>
              <p className="text-lead mb-10 max-w-2xl">
                PMS merges polished board interactions with enterprise-grade security, meaning your team can keep moving forward without sacrificing trust.
              </p>
              <div className="flex flex-wrap gap-4 mb-12">
                <Link to="/features" className="btn-primary">
                  Explore Features
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link to="/dashboard" className="btn-secondary">
                  Launch Demo
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="reveal stagger-1">
                    <p className="text-3xl font-black text-navy">{stat.value}</p>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal-right">
              <div className="card-marketing p-10 bg-navy text-sky relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <ShieldCheck className="w-32 h-32" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-8 text-sky">
                    <BadgeCheck className="w-6 h-6" />
                    <span className="font-bold uppercase tracking-widest text-sm">Security First</span>
                  </div>
                  <h3 className="text-3xl font-black mb-6 text-white">Trust is Built In</h3>
                  <div className="space-y-6">
                    {[
                      { icon: ShieldCheck, text: "Encrypted Credentials Vault" },
                      { icon: LayoutDashboard, text: "Granular Access Controls" },
                      { icon: Sparkles, text: "Audit-Ready Activity Logs" }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-sky" />
                        </div>
                        <span className="font-bold text-white">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <MarketingComparisonTable />
        
        <section className="shell-white section-animate">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {[
                {
                  title: "Built for Privacy",
                  desc: "Unlike other tools, we don't sell your data. Your project details and credentials remain yours alone.",
                  icon: ShieldCheck
                },
                {
                  title: "Lightning Performance",
                  desc: "Optimized for speed. No more waiting for boards to load or updates to sync across the team.",
                  icon: Activity
                },
                {
                  title: "No Per-Seat Pricing",
                  desc: "Scale your team without scaling your bill. One flat fee for everything you need.",
                  icon: Sparkles
                }
              ].map((item, i) => (
                <div key={i} className="card-marketing card-hover reveal stagger-1">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-navy" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingTestimonials />
        <MarketingFAQ />

        <section id="cta" className="shell-navy text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8">Ready to switch to a better workspace?</h2>
            <p className="text-lead text-blue-100 mb-12">
              Join the teams that left Trello and Asana for a more confident and secure workflow.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-secondary">
                Request a Demo
              </Link>
              <Link to="/dashboard" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                See the Dashboard
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default WhyTeamBoard;
